/*
  Babblee · product card gallery
  ===========================================================================
  Arrows on hover, dots for position, swipe on touch.

  Three things make this less trivial than it looks:

  1. The whole card is a link. `.bb-pc__title a::after` is stretched over the
     card, so anything that happens inside the frame is a click on the product
     page unless it is stopped. The arrows sit above it on z, so the browser
     already sends the click to the button — but a swipe that ends on the
     photograph still fires one, and a shopper who has just flicked through
     three images does not expect to arrive on a product page. So a gesture that
     moved suppresses the click that follows it.

  2. The secondary images ship without a src. Stacked slides are inside the
     viewport as soon as their card is, so `loading="lazy"` would not stop the
     browser fetching all five — 120 photographs to draw a grid of 24. They are
     promoted on the first hover, focus or touch, which is always before anyone
     can reach a control.

  3. Delegated from the document, so cards that arrive later — pagination,
     search, a section re-rendered by the theme editor — work without being
     told about.
*/
(() => {
  const FRAME = '[data-bb-gallery]';
  const SLIDE = '[data-bb-slide]';

  /* Resolving the frame from an event has to go up to the card and back down.

     The card's stretched link covers the photograph, so the element under a
     cursor or a finger over the media is that link — which lives in the body,
     and is not a descendant of the frame. `closest('[data-bb-gallery]')` on it
     returns null, so hydration on hover never ran and a swipe never started,
     both silently: the arrows still worked because clicking one hydrates, and
     the failure only showed as the first image arriving late. */
  const frameFrom = (target) => {
    if (!(target instanceof Element)) return null;
    const direct = target.closest(FRAME);
    if (direct) return direct;
    return target.closest('.bb-pc')?.querySelector(FRAME) || null;
  };

  /** Give the secondary images their src the first time a card is touched. */
  const hydrate = (frame) => {
    if (!frame || frame.dataset.bbHydrated) return;
    frame.dataset.bbHydrated = '1';
    for (const img of frame.querySelectorAll('img[data-bb-src]')) {
      if (img.dataset.bbSrcset) img.srcset = img.dataset.bbSrcset;
      img.src = img.dataset.bbSrc;
      img.removeAttribute('data-bb-src');
      img.removeAttribute('data-bb-srcset');
    }
  };

  const slidesOf = (frame) => [...frame.querySelectorAll(SLIDE)];

  /** Move to an absolute index, wrapping in both directions. */
  const goTo = (frame, next) => {
    const slides = slidesOf(frame);
    if (slides.length < 2) return;

    const from = Number(frame.dataset.bbIndex || 0);
    const to = ((next % slides.length) + slides.length) % slides.length;
    if (to === from) return;

    hydrate(frame);
    slides[from]?.classList.remove('is-active');
    slides[to]?.classList.add('is-active');

    const dots = frame.querySelectorAll('.bb-pc__dots span');
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === to));

    frame.dataset.bbIndex = String(to);
  };

  const step = (frame, delta) => goTo(frame, Number(frame.dataset.bbIndex || 0) + delta);

  /* Hydrate on the first sign of interest. `pointerover` covers mouse and pen;
     `focusin` covers the keyboard reaching the card; `touchstart` covers a
     finger, which never produces either. Passive: none of them are cancelled. */
  for (const type of ['pointerover', 'focusin', 'touchstart']) {
    document.addEventListener(type, (e) => hydrate(frameFrom(e.target)), { passive: true, capture: true });
  }

  document.addEventListener('click', (e) => {
    const button = e.target instanceof Element ? e.target.closest('[data-bb-nav]') : null;
    if (!button) return;
    const frame = button.closest(FRAME);
    if (!frame) return;
    // The arrow is above the card's stretched link, but say so anyway: this is
    // the difference between changing the photograph and leaving the page.
    e.preventDefault();
    e.stopPropagation();
    step(frame, Number(button.dataset.bbNav));
  });

  /* Swipe.
     Distance and direction are both required: a vertical drag is the page
     scrolling, and reading a horizontal component out of it would make the
     gallery change under a finger that was only trying to get down the grid. */
  const THRESHOLD = 40;
  let start = null;

  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return; // dragging with a mouse is selection, not a swipe
    const frame = frameFrom(e.target);
    if (!frame) return;
    start = { frame, x: e.clientX, y: e.clientY, moved: false };
  }, { passive: true });

  document.addEventListener('pointerup', (e) => {
    if (!start) return;
    const { frame, x, y } = start;
    const dx = e.clientX - x;
    const dy = e.clientY - y;
    start = null;

    if (Math.abs(dx) < THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    step(frame, dx < 0 ? 1 : -1);

    /* A swipe ends with a click on the photograph, which is a click on the
       card's stretched link. Swallow exactly the next one. */
    const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
    document.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 400);
  }, { passive: true });

  document.addEventListener('pointercancel', () => { start = null; }, { passive: true });
})();
